import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PopoverController, AlertController, LoadingController, ModalController, IonItemSliding } from '@ionic/angular';
import { TaskListItemPopover } from 'app/components/tasks/task-list-item/task-list-item.popover';
import { TaskModel } from 'app/models/dms/tasksSearchResult.model';
import { FieldModel } from 'app/models/shared/field.model';
import { FieldGroupModel } from 'app/models/shared/fieldGroup.model';
import { TaskService } from 'app/services/task.service';
import { UserService } from 'app/services/user.service';
import { ContactClass, ContactType, TaskStatus, TaskType } from 'app/utility/enums';
import { ModalDetailsEditComponent } from '../modal-details-edit/modal-details-edit.component';
import { ModalDetailsComponent } from '../modal-details/modal-details.component';
import { SelectContactSearchComponent } from '../select-contact/select-contact-search.component';
import { SelectListModalComponent } from '../select-list/select-list-modal.component';
import { ReminderListItemPopover } from './reminder-list-item.popover';

@Component({
    selector: 'app-reminder-list-item',
    templateUrl: './reminder-list-item.component.html',
    styleUrls: ['./reminder-list-item.component.scss'],
    standalone: false
})
export class ReminderListItemComponent implements OnInit
{

    @Input() reminderTask: TaskModel;
    @Input() display: 'card' | 'grid' = "card";
    @Input() type: 'data' | 'header' | 'skeleton' = "data";
    @Input() reminderTimes: any[] = [];
    @Output() reminderToRemove = new EventEmitter<TaskModel>();
    selectedTask: TaskModel;
    editActive: boolean = false;

    constructor(
        private userService: UserService,
        private taskService: TaskService,
        private alertController: AlertController,
        private loadingController: LoadingController,
        private modalController: ModalController,
        private popoverController: PopoverController
    ) { }

    ngOnInit()
    {

        if (this.userService.mcMinVersion("3.2.21"))
        {
            this.editActive = true;
        } else
        {
            this.editActive = false;
        }

        if (this.reminderTask)
        {
            this.reminderTask.startDate = new Date(this.reminderTask.startDate.toString().replace("Z", ""));
            this.getTaskReminderImage(this.reminderTask);
        }
    }

    async presentPopover(ev: any)
    {
        ev.stopPropagation();
        ev.preventDefault();
        if (!this.popoverController) return
        this.selectedTask = this.reminderTask;
        const popover = await this.popoverController.create({
            component: ReminderListItemPopover,
            componentProps: {
                task: this.reminderTask,
                editActive: this.editActive,
            },
            event: ev,
            translucent: true,
        });

        popover.onDidDismiss().then(
            (d: any) => this.handlePopoverDismiss(d)
        );
        return await popover.present();
    }

    handlePopoverDismiss = ({ data }) =>
    {
        switch (data)
        {
            case "view":
				this.selectTask(this.selectedTask.id);
                break;
            case "edit":
				this.selectTask(this.selectedTask.id, true);
                break;
            case "doTaskWithPrompts":
                this.doTask(this.selectedTask, true);
                break;
            case "doTaskWithOutPrompts":
                this.doTask(this.selectedTask, false);
                break;
            case "delegateTo":
                this.delegateTask(this.selectedTask);
                break;
            case "snoozeReminder":
                this.snoozeReminder(this.selectedTask);
                break;
            case "cancelTask":
                this.cancelTask(this.selectedTask);
                break;
            case "dismiss":
                this.dismissReminder(this.selectedTask);
                break;
            case "delete":
                this.deleteTask(this.selectedTask);
                break;
        }
    }

    async presentAlert(title: string, message: string, back: boolean = false)
    {
        this.userService.presentAlert(title, message, back);
    }
    
    async selectTask(taskId: string, edit: boolean = false)
	{
		const loading = await this.loadingController.create({
			message: 'Loading',
			duration: 60000
		});
		await loading.present();

		this.taskService.selectTask(taskId)
			.subscribe({
				next: async x =>
				{
					loading.dismiss();
					this.viewTask(x, edit);					
				},
				error: async err =>
				{
					loading.dismiss();
					this.userService.handleError(err);
				}
            });
	}

    async viewTask(task: TaskModel, edit: boolean = false) {
        var buttons = [
            {
                name: "editTask",
                label: "Edit task"
            },
            {
                name: "doTaskWithout",
                label: "Do task"
            },
            {
                name: "deleteTask",
                label: "Delete task"
            }

        ];

        var modal_field_groups: FieldGroupModel[] = [];
        let group1 = new FieldGroupModel("");

        group1.addField(new FieldModel("Task status", task.taskStatusId, "lookup", "taskStatusId", edit, true, task.taskStatus));
        task.startDate = new Date(task.startDate.toString().replace("Z", ""));
        group1.addField(new FieldModel("Due date", task.startDate, "datetime", "startDate", edit, true));
        group1.addField(new FieldModel("Assignee", task.assigneeId, "contact", "assigneeId", edit, true, task.assignee));
        group1.addField(new FieldModel("Category", task.taskColorId, "lookup", "taskColorId", edit, false, task.taskColor));
        group1.addField(new FieldModel("Priority", task.taskPriorityId, "lookup", "taskPriorityId", edit, true, task.taskPriority));
        group1.addField(new FieldModel("Reminder", task.reminderSecs, "select", "reminderSecs", edit, false, this.reminderTimes));
        group1.addField(new FieldModel("Title", task.name, "text", "name", edit, false));
        group1.addField(new FieldModel("Description", task.description, "longtext", "description", edit, false));
        
        group1.singleColumn = true;
        modal_field_groups.push(group1);
        const modalTask = await this.modalController.create({
            component: edit ? ModalDetailsEditComponent : ModalDetailsComponent,
            componentProps: {
                'title': task.taskType?.caption + (task.name ? ' • ' + task.name : ''),
                'field_groups': modal_field_groups,
                'edit': edit,
                'buttons': !edit ? buttons : [],
                'buttonsInPopover': true,
                'light': true,
            },
            cssClass: 'task-modal',
            initialBreakpoint: edit ? 1.0 : 0.75,
            breakpoints: [0, 0.75, 1]
        });
        await modalTask.present();
        const { data: taskData } = await modalTask.onDidDismiss();
        if (taskData && taskData.result == "editTask")
        {
            this.viewTask(task, true);
        }
        else if (taskData && taskData.result == "doTaskWithout")
        {
            this.doTask(task);
        }
        else if (taskData && taskData.result == "doTaskWith")
        {
            this.doTask(task, true);
        }
        else if (taskData && taskData.result == "deleteTask")
        {
            this.deleteTask(task);
        }
        else if (taskData && taskData.result && taskData.result.save)
        {
            this.syncFieldsWithObject(task, modal_field_groups);

            const loading = await this.loadingController.create({
                message: 'Saving',
                duration: 60000
            });
            await loading.present();

            this.taskService.updateTask(task.id, task)
                .subscribe({
                    next: x =>
                    {
                        loading.dismiss();
                    },
                    error: err =>
                    {
                        loading.dismiss();
                        this.userService.handleError(err);
                    }
                });
        }
    }

    syncFieldsWithObject(extra, modal_field_groups)
    {
        for (let field_group of modal_field_groups)
        {
            for (let field of field_group.fields)
            {
                if (extra[field.id] != null && extra[field.id] != field.value && !field.invalid && field.dirty)
                {
                    extra[field.id] = field.value;
                }
            }
        }
    }

    async doTask(task: TaskModel, withPrompts: boolean = false)
    {
        if (task.taskStatus.member == TaskStatus.Completed)
        {
            this.presentAlert("Task Information", "Task has already been completed.");
            return;
        }

        const loading = await this.loadingController.create({
            message: 'Completing',
            duration: 60000
        });
        await loading.present();


        this.taskService.completeTask(task.id, withPrompts)
            .subscribe({
                next: async x =>
                {
                    loading.dismiss();
                    this.reminderToRemove.emit(task);
                },
                error: async err =>
                {
                    loading.dismiss();
                    this.userService.handleError(err);
                }
            });
    }

    dismissReminder(task: TaskModel, event: any = null)
    {
        if (event)
        {
            event.stopPropagation();
        }

        task.reminderSecs = -1;

        const loading = this.loadingController.create({
            message: 'Dismissing',
            duration: 60000
        });
        loading.then(x => x.present());

        this.taskService.updateTask(task.id, task)
            .subscribe({
                next: async x =>
                {
                    loading.then(x => x.dismiss());
                    this.reminderToRemove.emit(task);
                },
                error: async err =>
                {
                    loading.then(x => x.dismiss());
                    this.userService.handleError(err);
                }
            });
    }

    async snoozeReminder(task: TaskModel, event: any = null)
    {
        if (event)
        {
            event.stopPropagation();
        }
        var items = this.reminderTimes;

        const modal = await this.modalController.create({
            component: SelectListModalComponent,
            componentProps: {
                'id': this.reminderTask.id,
                'title': 'Remind me again in',
                'items': items,
            }
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data && data.selectedItemId)
        {
            const loading = await this.loadingController.create({
                message: 'Snoozing',
                duration: 60000
            });
            await loading.present();

            this.taskService.snoozeReminder(task.id, data.selectedItemId)
                .subscribe({
                    next: async x =>
                    {
                        loading.dismiss();
                        this.reminderToRemove.emit(task);
                    },
                    error: async err =>
                    {
                        loading.dismiss();
                        this.userService.handleError(err);
                    }
                });
        }
    }

    async delegateTask(task: TaskModel)
    {
        const modal = await this.modalController.create({
            component: SelectContactSearchComponent,
            componentProps: {
                'title': "Delegate to",
                'contactClass': ContactClass.All,
                'contactType': ContactType.User,
                'placeholderText': "Search for a user",
            }
        });
        await modal.present();
        const { data } = await modal.onDidDismiss();
        if (data && data.contact)
        {
            task.assignee = data.contact;
            task.assigneeId = data.contact.id;


            const loading = await this.loadingController.create({
                message: 'Changing assignee',
                duration: 60000
            });
            await loading.present();


            this.taskService.updateTask(task.id, task)
                .subscribe({
                    next: async x =>
                    {
                        loading.dismiss();
                    },
                    error: async err =>
                    {
                        loading.dismiss();
                        this.userService.handleError(err);
                    }
                });
        }
    }

    async cancelTask(task: TaskModel)
    {
        if (task.taskStatus.member == TaskStatus.Cancelled)
        {
            this.presentAlert("Task Information", "Task has already been cancelled.");
            return;
        }

        var alert = await this.alertController.create({
            header: 'Task Information',
            message: 'Are you sure you want to cancel this task?',
            buttons: [
                {
                    text: 'No',
                    role: 'cancel',
                },
                {
                    text: 'Yes',
                    handler: async () =>
                    {
                        const loading = await this.loadingController.create({
                            message: 'Cancelling',
                            duration: 60000
                        });
                        await loading.present();
                
                
                        this.taskService.cancelTask(task.id)
                            .subscribe({
                                next: async updatedTask =>
                                {
                                    loading.dismiss();
                                    this.reminderTask.taskStatus.caption = "Cancelled";
                                },
                                error: async err =>
                                {
                                    loading.dismiss();
                                    this.userService.handleError(err);
                                }
                            });
                    }
                }
            ]
        });

        await alert.present();

    }

    async deleteTask(task: TaskModel)
    {

        var alert = await this.alertController.create({
            header: 'Task Information',
            message: 'Are you sure you want to delete this task?',
            buttons: [
                {
                    text: 'No',
                    role: 'cancel',
                },
                {
                    text: 'Yes',
                    handler: async () =>
                    {
                        const loading = await this.loadingController.create({
                            message: 'Deleting',
                            duration: 60000
                        });

                        await loading.present();

                        this.taskService.deleteTask(task.id)
                            .subscribe({
                                next: async x =>
                                {
                                    loading.dismiss();
                                    this.reminderToRemove.emit(task);
                                },
                                error: async err =>
                                {
                                    loading.dismiss();
                                    this.userService.handleError(err);
                                }
                            });
                    }
                }
            ]
        });

        await alert.present();
    }

    getTaskReminderImage(reminderTask: TaskModel)
    {
        reminderTask.imageUrl = "/assets/image/tasks/";
        switch (reminderTask.taskType.member)
        {

            case TaskType.RunExport:
                reminderTask.imageUrl += "IconTaskExport.png";
                break;
            case TaskType.PrintLetter:
                reminderTask.imageUrl += "IconTaskPrinter.png";
                break;
            case TaskType.SendEmail:
                reminderTask.imageUrl += "IconTaskEmail.png";
                break;
            case TaskType.GeneralTask:
                reminderTask.imageUrl += "IconTaskGeneral.png";
                break;
            case TaskType.SyncWorkshop:
                reminderTask.imageUrl += "IconTaskSyncWorkshop.png";
                break;
            case TaskType.ExecuteScript:
                reminderTask.imageUrl += "IconTaskScript.png";
                break;
            case TaskType.MarketingPrintLetter:
            case TaskType.MarketingSendEmail:
            case TaskType.MarketingSendSms:
                reminderTask.imageUrl += "IconTaskMarketing.png";
                break;
            case TaskType.MarketingError:
                reminderTask.imageUrl += "IconTaskMarketingError.png";
                break;
            case TaskType.BuyerScoreSendEmail:
                reminderTask.imageUrl += "IconTaskBuyerScore.png";
                break;
            default:
                reminderTask.imageUrl += "IconTaskGeneral.png";
                break;
        }		
    }


    bigSlidingIcons: boolean = false;
    
    chosenRatio: number;

    handleSlide(event: any, task: TaskModel, itemSliding: IonItemSliding)
    {
        let ratio = event.detail.ratio;
        this.bigSlidingIcons = (-1.75 > ratio || ratio > 1.75);

        var thresholdMet = (-2.25 > ratio || ratio > 2.25);
        if (thresholdMet)
        {
            this.chosenRatio = ratio;
            // console.log("Ratio is chosen: " + this.chosenRatio);
        }

        if (this.chosenRatio && (ratio == -1 || ratio == 1))
        {
            // console.log("Action this: " + this.chosenRatio);
            setTimeout(() =>
            {
                itemSliding.closeOpened();
            }, 250);
            if (this.chosenRatio < 0)
            {
                this.snoozeReminder(task);
            }
            else if (this.chosenRatio > 0)
            {
                this.dismissReminder(task);
            }

            this.chosenRatio = 0;
        }
    }
}
